import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Chip from "@material-ui/core/Chip";
import { Checkbox, Tooltip } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import AcceptTicketQueueModal from "../AcceptTicketQueueModal";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		borderLeft: "4px solid transparent",
		paddingTop: 8,
		paddingBottom: 8,
	},

	ticketUnread: {
		backgroundColor: "#eef6ff",
	},

	pendingTicket: {
		cursor: "unset",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	contactName: {
		minWidth: 0,
		flex: 1,
	},

	lastMessageTime: {
		marginLeft: 8,
	},

	contactLastMessage: {
		paddingRight: 20,
	},
	contactMeta: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		flexWrap: "wrap",
		marginTop: 2,
		marginBottom: 2,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	channelChip: {
		marginTop: 3,
		height: 18,
		fontSize: 10,
		maxWidth: 118,
		"& .MuiChip-label": {
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			paddingLeft: 7,
			paddingRight: 7,
		},
	},
	statusDot: {
		width: 7,
		height: 7,
		borderRadius: "50%",
		display: "inline-block",
		marginRight: 4,
		backgroundColor: green[500],
	},
	pendingDot: {
		backgroundColor: "#f9a825",
	},
}));

const TicketListItem = ({
	ticket,
	enableSelection = false,
	selected = false,
	onToggleSelect,
}) => {

	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [acceptModalOpen, setAcceptModalOpen] = useState(false);
	const ticketTags = Array.isArray(ticket.tags) && ticket.tags.length
		? ticket.tags
		: ticket.tag
			? [ticket.tag]
			: [];

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async (id, status) => {
		setLoading(true);
		try { 
			if (status === "pending") {
				setAcceptModalOpen(true);
				setLoading(false);
				return;
			}

			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={() => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
					[classes.ticketUnread]: ticket.unreadMessages > 0,
				})}
				style={{ borderLeftColor: ticket.queue?.color || "#7C7C7C" }}
			>
				{enableSelection && (
					<Checkbox
						checked={selected}
						onClick={(e) => e.stopPropagation()}
						onChange={(e) => onToggleSelect?.(ticket.id, e.target.checked)}
						color="primary"
						inputProps={{ "aria-label": `Selecionar ticket ${ticket.id}` }}
					/>
				)}
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem DP"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>

				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl} />
				</ListItemAvatar>

				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
								className={classes.contactName}
								style={{
									fontWeight: ticket.unreadMessages > 0 ? 700 : 400,
								}}
							>
								{ticket.contact.name}
							</Typography>

							{ticket.status !== "pending" && ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date())
										? format(parseISO(ticket.updatedAt), "HH:mm")
										: format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
								</Typography>
							)}

							{ticket.status === "pending" && (
								<ButtonWithSpinner
									color="primary"
									variant="contained"
									size="small"
									loading={loading}
									onClick={() => handleAcepptTicket(ticket.id, ticket.status)}
								>
									{i18n.t("ticketsList.buttons.accept")}
								</ButtonWithSpinner>
							)}
						</span>
					}
					secondary={
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div className={classes.contactMeta}>
								<Typography component="span" variant="caption" color="textSecondary">
									<span
										className={clsx(classes.statusDot, {
											[classes.pendingDot]: ticket.status === "pending",
										})}
									/>
									#{ticket.id}
								</Typography>
								{ticket.contact?.number && (
									<Typography component="span" variant="caption" color="textSecondary">
										{ticket.contact.number}
									</Typography>
								)}
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
								{ticketTags.slice(0, 3).map(tag => (
									<div key={tag.id || tag.name} style={{ marginBottom: 1 }}>
										<Tooltip title={tag.parent?.name ? `${tag.parent.name} / ${tag.name}` : tag.name}>
											<Chip
												label={tag.name}
												size="small"
												color="primary"
												className={classes.channelChip}
												style={{
													backgroundColor: tag.color || "#757575",
													color: "white",
													fontWeight: "bold"
												}}
											/>
										</Tooltip>
									</div>
								))}
								{ticketTags.length > 3 && (
									<Tooltip title={ticketTags.slice(3).map(tag => tag.name).join(", ")}>
										<Chip
											label={`+${ticketTags.length - 3}`}
											size="small"
											className={classes.channelChip}
										/>
									</Tooltip>
								)}
								{ticket.user?.name && ticket.status !== "pending" && (
									<div style={{ marginBottom: 1 }}>
										<Tooltip title={`Responsável: ${ticket.user.name}`}>
											<Chip
												label={ticket.user.name}
												size="small"
												color="primary"
												className={classes.channelChip}
											/>
										</Tooltip>
									</div>
								)}
								{ticket.queue?.name && ticket.status !== "pending" && (
									<div style={{ marginBottom: 1 }}>
										<Tooltip title={`Fila: ${ticket.queue.name}`}>
											<Chip
												label={ticket.queue.name}
												size="small"
												className={classes.channelChip}
												style={{
													backgroundColor: ticket.queue?.color || "#757575",
													color: "white",
													fontWeight: "bold"
												}}
											/>
										</Tooltip>
									</div>
								)}
								{ticket.whatsapp?.name && (
									<div style={{ marginBottom: 1 }}>
										<Tooltip title={`Instância: ${ticket.whatsapp.name}`}>
											<Chip
												label={ticket.whatsapp.name}
												size="small"
												className={classes.channelChip}
												style={{
													backgroundColor: ticket.whatsapp?.color || "#757575",
													color: "white",
													fontWeight: "bold"
												}}
											/>
										</Tooltip>
									</div>
								)}
							</div>
							<div style={{ display: "flex", alignItems: "center" }}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
									style={{ flex: 1 }}
								>
									{ticket.lastMessage ? (
										<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
									) : (
										<br />
									)}
								</Typography>

								{ticket.unreadMessages > 0 && (
										<Badge
											className={classes.newMessagesCount}
											badgeContent={ticket.unreadMessages}
											classes={{
												badge: classes.badgeStyle,
											}}
										/>
									)}
							</div>

						</div>
					}
				/>
			</ListItem>
			<AcceptTicketQueueModal
				modalOpen={acceptModalOpen}
				onClose={() => setAcceptModalOpen(false)}
				ticketId={ticket.id}
				user={user}
			/>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
